// Auction bid panel — opened with B at an on-auction building's door. Shows the live countdown,
// the current top bid and the minimum next bid, the player's bank balance (bids are HELD from the
// bank), and an amount input + Place-bid button. Same look as the building manage panel; re-rendered
// live as the synced auction state changes (world3d calls refreshAuctionBid) + a 1s countdown tick.

import { statsStore } from "../items/statsStore.js";
import { injectTheme } from "./theme.js";

export interface BidView {
  buildingId: string;
  buildingName: string;
  endsAt: number;        // epoch ms; <= now ⇒ the hammer fell (panel shows "ended")
  startPrice: number;    // minimum first bid
  minIncrement: number;  // minimum raise over the current bid
  bid: number;           // current top bid (0 = none yet)
  bidderName: string;    // current top bidder's display name
  iAmTopBidder: boolean; // the local player holds the top bid
}
export interface BidActions {
  bid: (amount: number) => void;
  close: () => void;
}

const STYLE_ID = "ghostland-auctionbid-style";
const OVERLAY_ID = "gl-auctionbid";
const CSS = `
#${OVERLAY_ID} { position: fixed; inset: 0; z-index: 36; display: none; align-items: center; justify-content: center;
  background: rgba(3,1,6,.55); font-family: ui-monospace, "SFMono-Regular", Menlo, monospace; }
#${OVERLAY_ID}.open { display: flex; }
#${OVERLAY_ID} .gl-ab-card { width: 360px; display: flex; flex-direction: column;
  background: var(--gl-panel, #14101c); border: 1px solid var(--gl-border, #3a3450); border-radius: 10px;
  box-shadow: 0 18px 60px rgba(0,0,0,.6); color: var(--gl-text, #f2f0f7); }
#${OVERLAY_ID} .gl-ab-head { display: flex; align-items: center; justify-content: space-between; padding: 14px 16px;
  border-bottom: 1px solid var(--gl-border, #3a3450); }
#${OVERLAY_ID} .gl-ab-title { font-size: 14px; letter-spacing: .04em; color: #e7c66a; }
#${OVERLAY_ID} .gl-ab-x { cursor: pointer; color: var(--gl-muted, #9a93b0); font-size: 16px; line-height: 1; padding: 2px 6px; }
#${OVERLAY_ID} .gl-ab-x:hover { color: var(--gl-text, #fff); }
#${OVERLAY_ID} .gl-ab-body { padding: 12px 16px 16px; }
#${OVERLAY_ID} .gl-ab-clock { text-align: center; font-size: 13px; color: #e7c66a; padding: 6px 0 10px; letter-spacing: .06em; }
#${OVERLAY_ID} .gl-ab-row { display: flex; align-items: center; justify-content: space-between; gap: 8px;
  padding: 7px 10px; border-radius: 7px; background: rgba(255,255,255,.03); margin-bottom: 5px; font-size: 12px; }
#${OVERLAY_ID} .gl-ab-row .lbl { color: var(--gl-muted, #9a93b0); letter-spacing: .04em; }
#${OVERLAY_ID} .gl-ab-row .val { font-weight: 700; }
#${OVERLAY_ID} .gl-ab-row .val.you { color: #8de89a; }
#${OVERLAY_ID} .gl-ab-field { display: flex; align-items: center; gap: 8px; margin: 12px 0 0; }
#${OVERLAY_ID} .gl-ab-field label { font-size: 11px; color: var(--gl-muted, #9a93b0); letter-spacing: .04em; white-space: nowrap; }
#${OVERLAY_ID} .gl-ab-field input { flex: 1; min-width: 0; padding: 7px 10px; font: inherit; font-size: 13px; text-align: right;
  color: var(--gl-text, #f2f0f7); background: var(--gl-panel-2, #1d1828); border: 1px solid var(--gl-border, #3a3450); border-radius: 6px; outline: none; }
#${OVERLAY_ID} .gl-ab-field input:focus { border-color: #e7c66a; }
#${OVERLAY_ID} .gl-ab-err { font-size: 11px; color: #f29a9a; margin-top: 7px; display: none; }
#${OVERLAY_ID} .gl-ab-go { width: 100%; margin-top: 10px; cursor: pointer; font-size: 12px; letter-spacing: .06em;
  padding: 10px; border-radius: 7px; border: 1px solid #5a4a26; background: rgba(120,95,30,.18); color: #e7c66a; }
#${OVERLAY_ID} .gl-ab-go:hover { border-color: #e7c66a; }
#${OVERLAY_ID} .gl-ab-go:disabled { opacity: .4; cursor: default; }
#${OVERLAY_ID} .gl-ab-ended { text-align: center; font-size: 13px; color: var(--gl-muted, #9a93b0); padding: 14px 0 6px; }
`;

/** Format the time left as "12h 34m 05s" (never negative). */
function fmtLeft(endsAt: number): string {
  const s = Math.max(0, Math.floor((endsAt - Date.now()) / 1000));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), ss = s % 60;
  return `${h}h ${String(m).padStart(2, "0")}m ${String(ss).padStart(2, "0")}s`;
}

let overlay: HTMLDivElement | null = null;
let acts: BidActions | null = null;
let openId = "";
let lastView: BidView | null = null;
let ticker: number | null = null; // 1s re-render (countdown + live bank balance)

function ensure(): void {
  if (overlay) return;
  injectTheme();
  if (!document.getElementById(STYLE_ID)) {
    const s = document.createElement("style"); s.id = STYLE_ID; s.textContent = CSS; document.head.appendChild(s);
  }
  overlay = document.createElement("div");
  overlay.id = OVERLAY_ID;
  overlay.innerHTML = `<div class="gl-ab-card"><div class="gl-ab-head"><div class="gl-ab-title"></div><div class="gl-ab-x">✕</div></div><div class="gl-ab-body"></div></div>`;
  document.body.appendChild(overlay);
  overlay.addEventListener("click", (e) => { if (e.target === overlay) closeAuctionBid(); });
  overlay.querySelector(".gl-ab-x")!.addEventListener("click", () => closeAuctionBid());
}

function render(view: BidView): void {
  if (!overlay) return;
  lastView = view;
  const esc = (s: string): string => s.replace(/[<>&]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[c]!));
  overlay.querySelector(".gl-ab-title")!.textContent = `AUCTION · ${view.buildingName}`;
  const body = overlay.querySelector(".gl-ab-body") as HTMLDivElement;
  const over = Date.now() >= view.endsAt;
  const minBid = view.bid > 0 ? view.bid + view.minIncrement : view.startPrice;
  const bank = statsStore.get().bankMoney;
  // A live re-render (bid sync / countdown tick) must not wipe an amount the player already typed.
  const prev = (overlay.querySelector(".gl-ab-amount") as HTMLInputElement | null)?.value;
  const topRow = view.bid > 0
    ? `<div class="gl-ab-row"><span class="lbl">Top bid</span><span class="val${view.iAmTopBidder ? " you" : ""}">$${view.bid.toLocaleString()} · ${view.iAmTopBidder ? "you" : esc(view.bidderName || "?")}</span></div>`
    : `<div class="gl-ab-row"><span class="lbl">Top bid</span><span class="val">none yet — opens at $${view.startPrice.toLocaleString()}</span></div>`;
  body.innerHTML = `
    <div class="gl-ab-clock">${over ? "HAMMER DOWN" : `Ends in ${fmtLeft(view.endsAt)}`}</div>
    ${topRow}
    <div class="gl-ab-row"><span class="lbl">Minimum raise</span><span class="val">$${view.minIncrement.toLocaleString()}</span></div>
    <div class="gl-ab-row"><span class="lbl">Minimum bid</span><span class="val">$${minBid.toLocaleString()}</span></div>
    <div class="gl-ab-row"><span class="lbl">Your bank</span><span class="val">$${bank.toLocaleString()}</span></div>
    ${over
      ? `<div class="gl-ab-ended">The auction has ended.</div>`
      : `<div class="gl-ab-field"><label>Your bid ($)</label><input class="gl-ab-amount" type="number" min="${minBid}" step="1" value="${prev ?? String(minBid)}"></div>
         <div class="gl-ab-err"></div>
         <button class="gl-ab-go">Place bid — held from your bank</button>`}
  `;
  const go = body.querySelector(".gl-ab-go") as HTMLButtonElement | null;
  if (go) go.onclick = () => {
    const input = body.querySelector(".gl-ab-amount") as HTMLInputElement;
    const err = body.querySelector(".gl-ab-err") as HTMLDivElement;
    const amount = Math.floor(Number(input.value));
    const fail = (text: string): void => { err.textContent = text; err.style.display = "block"; };
    if (!Number.isFinite(amount) || amount < minBid) { fail(`Bid at least $${minBid.toLocaleString()}.`); return; }
    if (amount > bank + (view.iAmTopBidder ? view.bid : 0)) { fail("Not enough bank money to cover that bid."); return; }
    err.style.display = "none";
    acts?.bid(amount);
  };
}

/** Open the bid panel for an on-auction building. */
export function openAuctionBid(view: BidView, actions: BidActions): void {
  ensure();
  acts = actions; openId = view.buildingId;
  render(view);
  overlay!.classList.add("open");
  if (ticker === null) ticker = window.setInterval(() => { if (lastView) render(lastView); }, 1000);
}
/** Re-render with fresh data (called when the synced auction state changes). */
export function refreshAuctionBid(view: BidView): void {
  if (overlay?.classList.contains("open") && view.buildingId === openId) render(view);
}
export function closeAuctionBid(): void {
  if (!overlay?.classList.contains("open")) return;
  if (ticker !== null) { clearInterval(ticker); ticker = null; }
  lastView = null;
  overlay.classList.remove("open"); openId = ""; const a = acts; acts = null; a?.close();
}
export function isAuctionBidOpen(): boolean { return !!overlay?.classList.contains("open"); }
export function auctionBidId(): string { return openId; }
